import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import getMetadata from '@/shared/api/product/getMetadata';
import viewProducts from '@/shared/api/product/viewProducts';
import useDataGridTable from '@/shared/components/dataDisplay/DataGridTable/hooks/useDataGridTable';
import useTableColumnVisibility from '@/shared/components/dataDisplay/DataGridTable/hooks/useTableColumnVisibility';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import MetadataDefinitions from '@/shared/types/metadata/metadataDefinitions.type';
import Product from '@/shared/types/products/product.type';

const getProductMetadataKeys = (
  metadata_definitions: MetadataDefinitions[],
  requiredLevel: 'REQUIRED' | null = null,
) => {
  return metadata_definitions
    .filter((item) => item.level_constraint === 'PRODUCT')
    .filter((item) =>
      requiredLevel ? item.required_level === requiredLevel : true,
    )
    .filter(
      (item) =>
        !/dimension|json|money|rating|volume|weight/.test(item.metatype_id),
    )
    .map((item) => item.metadata_key);
};

const getRestrictedColumns = (metadata_definitions: MetadataDefinitions[]) => {
  return metadata_definitions
    .filter((item) =>
      /dimension|json|money|rating|volume|weight/.test(item.metatype_id),
    )
    .map((item) => item.metadata_key);
};

const useProductsTable_v2 = () => {
  const redirectLogin = useRedirectLogin();

  const { apiRef, model, requestBody } = useDataGridTable();
  const requestBodyRef = useRef<typeof requestBody | null>(null);

  const [table, setTable] = useState({
    data: [] as Product[],
    totalRows: 0,
    isLoading: false,
  });

  const [tableColumnsList, setTableColumnsList] = useState<string[]>([]);
  const [tableRequiredColumns, setTableRequiredColumns] = useState<string[]>(
    [],
  );
  const [tableRestrictedColumns, setTableRestrictedColumns] = useState<
    string[]
  >([]);

  const { columns, visibilityModel, setVisibilityModel } =
    useTableColumnVisibility({
      tableColumnsList,
      tableRequiredColumns,
    });

  useEffect(() => {
    const fetchMetadata = async () => {
      const response = await getMetadata(() => redirectLogin());

      if (response.success) {
        const productColumns = getProductMetadataKeys(
          response.data.metadata_definitions,
        );

        const order = ['product_name'];

        const requiredColumns = getProductMetadataKeys(
          response.data.metadata_definitions,
          'REQUIRED',
        ).sort((a, b) => {
          const indexA = order.indexOf(a);
          const indexB = order.indexOf(b);

          if (indexA === -1 && indexB === -1) {
            return a.localeCompare(b);
          }
          if (indexA === -1) {
            return 1;
          }
          if (indexB === -1) {
            return -1;
          }
          return indexA - indexB;
        });

        const restrictedColumns = getRestrictedColumns(
          response.data.metadata_definitions,
        );

        setTableColumnsList(['product_id', ...productColumns]);
        setTableRequiredColumns(['product_id', ...requiredColumns, 'actions']);
        setTableRestrictedColumns(['variants', ...restrictedColumns]);
      }
    };

    void fetchMetadata();
  }, [redirectLogin]);

  useEffect(() => {
    const fetchProducts = async () => {
      setTable((prev) => ({ ...prev, isLoading: true }));

      const response = await viewProducts({
        body: requestBody,
        onUnauthorized: redirectLogin,
      });

      setTable((prev) => ({ ...prev, isLoading: false }));

      if (response.success) {
        const dataFiltered = response.data.map((item) => {
          const filteredItem = { ...item };

          tableRestrictedColumns.forEach((column) => {
            delete filteredItem[column];
          });

          return filteredItem;
        });

        setTable((prev) => ({
          ...prev,
          data: dataFiltered,
          totalRows: response.total_records || 0,
        }));
      }
    };

    if (!isEqual(requestBody, requestBodyRef.current)) {
      requestBodyRef.current = requestBody;

      void fetchProducts();
    }
  }, [redirectLogin, requestBody, tableRestrictedColumns]);

  return {
    apiRef,
    model,
    table,
    columns,
    visibilityModel,
    setVisibilityModel,
  };
};

export default useProductsTable_v2;
